import { makeObservable, observable } from "mobx";

import { BlockData, BlockModel, BlockRemoteData } from "@courses-models/course/src/ts/models/BlockModel";

/**
 * Specification of raw data required to create a new LinkBlockModel.
 */
type LinkBlockData = BlockData & {
    href: string;
    description: string;
};

/**
 * Specification of the format used when serializing link block content for
 * storage in the remote backend.
 */
type LinkBlockRemoteData = {
    description: string;
    href: string;
};

/**
 * Link blocks basically contain a link seleected by the teacher, optionally
 * along side a description of this link.
 */
export class LinkBlockModel extends BlockModel {
    /**
     * Contains the absolute link to the page, that should be opened when the
     * user clicks this link.
     */
    public href: string;

    /**
     * Contains a description, as entered by the teacher, that should be
     * presented to students along side this link.
     */
    public description: string;

    /**
     * Apply initial data when the model is instantiated.
     */
    constructor(initial: LinkBlockData) {
        super(initial);

        this.href = initial.href;
        this.description = initial.description;

        makeObservable(this, {
            title: observable,
            note: observable,
            href: observable,
            description: observable,
        })
    }

    /**
     * Helper that'll create an instance of the LinkBlockModel based on a
     * serialized data from the remote backend.
     */
    public static DESERIALIZE(data: BlockRemoteData): LinkBlockModel {
        const content = <LinkBlockRemoteData>JSON.parse(data.content);

        return new LinkBlockModel({
            id: data.id,
            note: {
                content: data.note.content,
                enabled: data.note.isVisible,
            },
            title: data.title,
            ...content,
        });
    }

    /**
     * Helper that'll return a serialized version of the content, so that it can
     * be stored remotely.
     */
    public serializeContent(): string {
        return JSON.stringify({
            description: this.description,
            href: this.href,
        });
    }

    /**
     * Helper that'll generate a snapshot of the content within this block
     */
    protected getContentSnapshot(): string {
        return JSON.stringify({
            href: this.href,
            description: this.description,
        });
    }
}
